import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";

/**
 * Credential expiry banner — shows when a coach's certification or insurance
 * is within 30 days (warning), within 7 days (critical), or already lapsed.
 *
 * Used in two places:
 *  - CoachDashboard (own credentials, defaults `userId` to undefined → /me)
 *  - AdminUserDetail (admin viewing a coach, pass that coach's id)
 *
 * Renders nothing when everything's healthy or no data exists.
 */
export default function CredentialExpiryBanner({ userId, compact = false, refreshKey = 0 }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = userId
          ? `/coach/credentials-status?user_id=${encodeURIComponent(userId)}`
          : `/coach/credentials-status`;
        const { data } = await api.get(url);
        if (!cancelled) setStatus(data);
      } catch (_e) { /* silent — banner just doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [userId, refreshKey]);

  if (!status) return null;
  if (!["expired", "critical", "warning"].includes(status.worst)) return null;

  const items = [status.cert, status.insurance].filter(
    (x) => ["expired", "critical", "warning"].includes(x.state)
  );
  if (!items.length) return null;

  const overrideActive = !!status.override?.active;
  const tone = overrideActive
    ? { border: "#CCFF00", bg: "#0a1a05", text: "#E5FFB0", accent: "#CCFF00", icon: CheckCircle2 }
    : (status.worst === "expired" || status.worst === "critical"
        ? { border: "#FF4500", bg: "#1a0a05", text: "#FFD3C2", accent: "#FF4500", icon: ShieldAlert }
        : { border: "#FFC107", bg: "#1a1304", text: "#FFE9A8", accent: "#FFC107", icon: AlertTriangle });
  const Icon = tone.icon;

  return (
    <div
      data-testid="credential-expiry-banner"
      className="border-l-4 px-4 py-3 flex items-start gap-3"
      style={{ borderColor: tone.border, background: tone.bg, color: tone.text }}
    >
      <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: tone.accent }} />
      <div className="flex-1">
        <div className="font-bold mb-1" style={{ color: tone.accent }}>
          {overrideActive
            ? "Credentials lapsed — admin override active"
            : (status.worst === "expired" ? "Credentials expired" : status.worst === "critical" ? "Credentials expiring this week" : "Credentials expiring soon")}
        </div>
        <ul className={compact ? "text-xs space-y-0.5" : "text-sm space-y-1"}>
          {items.map((x) => (
            <li key={x.key} data-testid={`expiry-${x.key}`}>
              <span className="font-semibold">{x.label}</span>{" "}
              {x.state === "expired"
                ? <>expired {Math.abs(x.days_until)} day{Math.abs(x.days_until) === 1 ? "" : "s"} ago</>
                : <>expires in {x.days_until} day{x.days_until === 1 ? "" : "s"}</>}
              {x.expiry && <span className="text-[#A3A3A3]"> · {x.expiry}</span>}
            </li>
          ))}
        </ul>
        {!compact && (
          <div className="text-xs mt-2 text-[#A3A3A3]">
            {overrideActive
              ? <>An admin has allowed bookings to continue{status.override?.expires_at ? <> until <span className="text-white">{status.override.expires_at}</span></> : null}{status.override?.reason ? <> — {status.override.reason}</> : null}. Reminders still go out daily.</>
              : "Reminder emails are sent automatically at 30, 14, 7, and 1 day before expiry — and daily once lapsed."}
          </div>
        )}
      </div>
    </div>
  );
}

/** Small inline pill version — for use inside tables or cards. */
export function CredentialExpiryPill({ userId, refreshKey = 0 }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = userId
          ? `/coach/credentials-status?user_id=${encodeURIComponent(userId)}`
          : `/coach/credentials-status`;
        const { data } = await api.get(url);
        if (!cancelled) setStatus(data);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [userId, refreshKey]);
  if (!status) return null;
  if (status.worst === "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#CCFF00]" data-testid="expiry-pill-ok">
        <CheckCircle2 className="w-3 h-3" /> Credentials current
      </span>
    );
  }
  const colour = status.worst === "expired" || status.worst === "critical" ? "#FF4500" : "#FFC107";
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold"
      style={{ color: colour }} data-testid="expiry-pill-warn">
      <AlertTriangle className="w-3 h-3" /> {status.worst}
    </span>
  );
}
